import React, { Fragment, useEffect, useState } from "react";
import {
  ButtonGroup,
  Col,
  DropdownItem,
  DropdownMenu,
  DropdownToggle,
  Row,
  Table,
  UncontrolledDropdown,
  Spinner,
} from "reactstrap";
import { Link } from "react-router-dom";
import {
  useReactTable,
  getCoreRowModel,
  getFilteredRowModel,
  getPaginationRowModel,
  getSortedRowModel,
  flexRender,
  SortingState,
} from "@tanstack/react-table";
import { rankItem } from "@tanstack/match-sorter-utils";
import { RiSearchLine } from "react-icons/ri";
import "../../App.css";

interface TableContainerProps {
  columns: any[];
  data: any[];
  isGlobalFilter?: boolean;
  tableLoader?: boolean;
  customPageSize: number;
  tableClass?: string;
  numberOfRows?: any;
  theadClass?: string;
  divClass?: string;
  SearchPlaceholder?: string;
  currentPage: number;
  totalPages: number;
  totalRecords: number;
  onPageChange: (page: number) => void;
  onPageSizeChange: (pageSize: number) => void;
  onSearch?: (value: string) => void;
  onSort?: (column: string, direction: "asc" | "desc") => void;
  manualPagination?: boolean;
}

interface DebouncedInputProps {
  value: string;
  onChange: (value: string) => void;
  debounce?: number;
  placeholder?: string;
  [key: string]: any;
}

const DebouncedInput: React.FC<DebouncedInputProps> = ({
  value: initialValue,
  onChange,
  debounce = 500,
  ...props
}) => {
  const [value, setValue] = useState(initialValue);

  useEffect(() => {
    setValue(initialValue);
  }, [initialValue]);

  useEffect(() => {
    const timeout = setTimeout(() => {
      onChange(value);
    }, debounce);

    return () => clearTimeout(timeout);
  }, [value, onChange, debounce]);

  return (
    <input
      {...props}
      value={value}
      id="search-bar-0"
      className="form-control shadow-none search"
      onChange={(e) => setValue(e.target.value)}
      onKeyDown={(e) => e.key === "Enter" && e.preventDefault()}
    />
  );
};

const TableContainer: React.FC<TableContainerProps> = ({
  columns,
  data,
  isGlobalFilter,
  numberOfRows,
  tableLoader,
  customPageSize,
  tableClass,
  theadClass,
  divClass,
  SearchPlaceholder,
  currentPage,
  totalPages,
  totalRecords,
  onPageChange,
  onPageSizeChange,
  onSearch,
  onSort,
  manualPagination,
}) => {
  const pageSizesArray = [5, 10, 25, 50, 100];
  const [globalFilter, setGlobalFilter] = useState<string>("");
  const [pageSize, setPageSize] = useState<number>(customPageSize);
  const [sorting, setSorting] = useState<SortingState>([]);
  const [order, setOrder] = useState<{ [key: string]: string }>({});

  const table = useReactTable({
    columns,
    data,
    filterFns: {
      fuzzy: (row, columnId, value, addMeta) => {
        const itemRank = rankItem(row.getValue(columnId), value);
        addMeta({ itemRank });
        return itemRank.passed;
      },
    },
    state: {
      globalFilter,
      sorting,
    },
    onGlobalFilterChange: setGlobalFilter,
    onSortingChange: setSorting,
    getCoreRowModel: getCoreRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
    getPaginationRowModel: getPaginationRowModel(),
    getSortedRowModel: getSortedRowModel(),
    initialState: {
      pagination: { pageSize: customPageSize },
    },
  });

  const handleNextPage = () => {
    onPageChange(currentPage + 1);
  };

  const handlePreviousPage = () => {
    onPageChange(currentPage - 1);
  };

  const handleSorting = (column: any) => {
    const columnName = column.accessorKey;
    const isSorted = sorting?.find((sort) => sort.id === columnName);
    const newDirection = isSorted ? (isSorted.desc ? "asc" : "desc") : "asc";

    setSorting([{ id: columnName, desc: newDirection === "desc" }]);
    setOrder({ [columnName]: newDirection });

    if (onSort) {
      onSort(columnName, newDirection);
    }
  };

  const renderPageNumbers = () => {
    const MAX_VISIBLE_PAGES = 5;
    let startPage = Math.max(1, currentPage - 2);
    let endPage = Math.min(totalPages, startPage + MAX_VISIBLE_PAGES - 1);

    if (endPage - startPage + 1 < MAX_VISIBLE_PAGES) {
      startPage = Math.max(1, endPage - MAX_VISIBLE_PAGES + 1);
    }

    const pageNumbers = [];

    if (startPage > 1) {
      pageNumbers.push(
        <li key="ellipsisStart" className="page-item disabled">
          <span className="page-link">...</span>
        </li>
      );
    }

    for (let i = startPage; i <= endPage; i++) {
      pageNumbers.push(
        <li
          key={i}
          className={currentPage === i ? "page-item active" : "page-item"}
        >
          <Link
            className="page-link page-success"
            onClick={(e) => {
              e.preventDefault();
              onPageChange(i);
            }}
            to="#"
          >
            {i}
          </Link>
        </li>
      );
    }

    if (endPage < totalPages) {
      pageNumbers.push(
        <li key="ellipsisEnd" className="page-item disabled">
          <span className="page-link">...</span>
        </li>
      );
    }

    return pageNumbers;
  };

  const handlePageSizeChange = (newSize: number) => {
    setPageSize(newSize);
    onPageSizeChange(newSize);
    onPageChange(1);
    table.setPageSize(newSize);
  };

  const startIndex = (currentPage - 1) * pageSize + 1;
  const endIndex = Math.min(currentPage * pageSize, numberOfRows);
  return (
    <Fragment>
      {isGlobalFilter && (
        <Row className="mb-1">
          <Row className="d-flex gap-2 justify-content-between align-items-center">
            <Col sm={4} className="text-start pagenumber">
              Items per page: &nbsp;
              <ButtonGroup>
                <UncontrolledDropdown>
                  <DropdownToggle
                    tag="button"
                    className="btn btn-link border px-2 py-1 shadow-none text-dark"
                  >
                    {pageSize} <i className="mdi mdi-chevron-down ms-1"></i>
                  </DropdownToggle>
                  <DropdownMenu className="minimumWidth">
                    {pageSizesArray.map((size) => (
                      <DropdownItem
                        key={size}
                        onClick={() => handlePageSizeChange(size)}
                      >
                        {size}
                      </DropdownItem>
                    ))}
                  </DropdownMenu>
                </UncontrolledDropdown>
              </ButtonGroup>
            </Col>

            <Col sm={3}>
              <div className="search-box me-2 mb-2 d-inline-block col-12 position-relative">
                <DebouncedInput
                  className="form-control"
                  value={globalFilter ?? ""}
                  onChange={(value) => {
                    setGlobalFilter(value);
                    if (onSearch) onSearch(value);
                  }}
                  placeholder={SearchPlaceholder || "Search..."}
                />
                <i className="mdi mdi-magnify position-absolute top-50 start-0 translate-middle-y ms-3"></i>
              </div>
            </Col>
          </Row>
        </Row>
      )}

      <div className={`${divClass} overflow-x-auto`}>
        {tableLoader ? (
          <Table hover className={tableClass}>
            <thead>
              {table.getHeaderGroups().map((headerGroup) => (
                <tr key={headerGroup.id}>
                  {headerGroup.headers.map((header) => (
                    <th
                      key={header.id}
                      onClick={() => {
                        if (header.column.getCanSort()) {
                          handleSorting(header.column.columnDef);
                        }
                      }}
                    >
                      {flexRender(
                        header.column.columnDef.header,
                        header.getContext()
                      )}
                      {order[header.column.id] === "asc" && " ▲"}
                      {order[header.column.id] === "desc" && " ▼"}
                    </th>
                  ))}
                </tr>
              ))}
            </thead>
            <tbody></tbody>
          </Table>
        ) : (
          <Table hover className={tableClass}>
            <thead className={theadClass}>
              {table.getHeaderGroups().map((headerGroup) => (
                <tr key={headerGroup.id}>
                  {headerGroup.headers.map((header) => (
                    <th
                      key={header.id}
                      onClick={() => {
                        if (header.column.getCanSort()) {
                          handleSorting(header.column.columnDef);
                        }
                      }}
                    >
                      {flexRender(
                        header.column.columnDef.header,
                        header.getContext()
                      )}
                      {order[header.column.id] === "asc" && " ▲"}
                      {order[header.column.id] === "desc" && " ▼"}
                    </th>
                  ))}
                </tr>
              ))}
            </thead>

            <tbody>
              {data.length > 0 ? (
                table.getRowModel().rows.map((row) => (
                  <tr key={row.id}>
                    {row.getVisibleCells().map((cell) => (
                      <td key={cell.id}>
                        {flexRender(
                          cell.column.columnDef.cell,
                          cell.getContext()
                        )}
                      </td>
                    ))}
                  </tr>
                ))
              ) : (
                <tr>
                  <td colSpan={columns.length} className="text-center">
                    <div className="py-4 text-center">
                      <div>
                        <span className="fs-1 text-success">
                          <RiSearchLine />
                        </span>
                      </div>
                      <div className="mt-4">
                        <h5>Data Not Found</h5>
                        <p className="text-muted">
                          The data you are looking for is not available.
                        </p>
                      </div>
                    </div>
                  </td>
                </tr>
              )}
            </tbody>
          </Table>
        )}
      </div>

      {manualPagination && data?.length > 0 && (
        <Row className="align-items-center mt-2 g-3 text-center text-sm-start pagenumber">
          <div className="col-sm">
            <div className="text-muted">
              Showing{" "}
              <span className="fw-semibold ms-1">
                {startIndex} to {endIndex}
              </span>{" "}
              of <span className="fw-semibold">{totalRecords}</span> Results
            </div>
          </div>
          <div className="col-sm-auto">
            <ul className="pagination pagination-separated pagination-sm justify-content-center justify-content-sm-start mb-0">
              <li
                className={
                  currentPage === 1 ? "page-item disabled" : "page-item"
                }
              >
                <Link
                  className="page-link p-0"
                  to="#"
                  onClick={(e) => {
                    e.preventDefault();
                    handlePreviousPage();
                  }}
                >
                  <i className="mdi mdi-chevron-left fs-4"></i>
                </Link>
              </li>
              {renderPageNumbers()}
              <li
                className={
                  currentPage === totalPages
                    ? "page-item disabled"
                    : "page-item"
                }
              >
                <Link
                  className="page-link p-0"
                  to="#"
                  onClick={(e) => {
                    e.preventDefault();
                    handleNextPage();
                  }}
                >
                  <i className="mdi mdi-chevron-right fs-4"></i>
                </Link>
              </li>
            </ul>
          </div>
        </Row>
      )}
    </Fragment>
  );
};

export default TableContainer;
